import React, { useEffect } from 'react';
import { useDashboard } from '../../contexts/DashboardContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTabs } from '../../hooks/useTabs';
import { useUsersSectionPrefetch } from '../../hooks/useUsersSectionPrefetch';
import { hasVersionFilter } from '../../utils/versionFilter';
import VersionUnavailable from '../widgets/VersionUnavailable';
import TabsContainer from './TabsContainer';
import RegistrationsWidget from '../widgets/RegistrationsWidget';
import CampaignsWidget from '../widgets/CampaignsWidget';
import CreativesWidget from '../widgets/CreativesWidget';
import RevenueWidget from '../widgets/RevenueWidget';
import LtvWidget from '../widgets/LtvWidget';
import LtvTimeWidget from '../widgets/LtvTimeWidget';
import RoasWidget from '../widgets/RoasWidget';
import RoasTimeWidget from '../widgets/RoasTimeWidget';
import RoasMonthlySummaryWidget from '../widgets/RoasMonthlySummaryWidget';
import ActiveUsersWidget from '../widgets/ActiveUsersWidget';
import ArpdauWidget from '../widgets/ArpdauWidget';
import WhalesWidget from '../widgets/WhalesWidget';
import SessionsAnalyticsWidget from '../widgets/SessionsAnalyticsWidget';
import ProgressionLevelWidget from '../widgets/ProgressionLevelWidget';
import ProgressionTowerWidget from '../widgets/ProgressionTowerWidget';
import StartFunnelWidget from '../widgets/StartFunnelWidget';
import LevelFunnelWidget from '../widgets/LevelFunnelWidget';
import LevelWinrateByDayWidget from '../widgets/LevelWinrateByDayWidget';
import RetentionCohortWidget from '../widgets/RetentionCohortWidget';
import RetentionCurveWidget from '../widgets/RetentionCurveWidget';
import RetentionByPeriodWidget from '../widgets/RetentionByPeriodWidget';
import TopIapWidget from '../widgets/TopIapWidget';
import ResourceFlowWidget from '../widgets/ResourceFlowWidget';
import AdHealthWidget from '../widgets/ads/AdHealthWidget';
import AdFunnelWidget from '../widgets/ads/AdFunnelWidget';
import AdPlacementWidget from '../widgets/ads/AdPlacementWidget';
import AdLevelsTab from '../widgets/ads/AdLevelsTab';
import AdLtvWidget from '../widgets/ads/AdLtvWidget';
import AdSourceWidget from '../widgets/ads/AdSourceWidget';
import AdSegmentsWidget from '../widgets/ads/AdSegmentsWidget';
import AdVersionWidget from '../widgets/ads/AdVersionWidget';
import AdRewardWidget from '../widgets/ads/AdRewardWidget';
import AdFrequencyWidget from '../widgets/ads/AdFrequencyWidget';
import AdsSectionFilters from '../widgets/ads/AdsSectionFilters';
import { AdsGeoProvider } from '../../hooks/useAdsGeo';
import AbCompareWidget from '../widgets/abtest/AbCompareWidget';
import PrototypesOverview from '../widgets/PrototypesOverview';
import AbsorptionGameWidget from '../widgets/AbsorptionGameWidget';
import { Calendar, Target, DollarSign, TrendingUp, BarChart3, Clock, Activity, Sparkles, Users, LineChart, Crown, ShoppingCart, Gem, Gauge, Filter, MapPin, Layers, Coins, Repeat, FlaskConical, Smartphone, Gift, RotateCw } from 'lucide-react';

export default function ContentArea() {
  const { activeSection, setActiveSection, selectedClientVersions, selectedInstallVersions } = useDashboard();
  const { user, loading: authLoading } = useAuth();

  // Сохранённый в localStorage/URL раздел «Прототипы» без доступа открывает раздел по умолчанию.
  // Пока пользователь не загружен, ничего не сбрасываем.
  const isPrototypesDenied = activeSection === 'prototypes' && !authLoading && !user?.canViewPrototypes;
  useEffect(() => {
    if (isPrototypesDenied) setActiveSection('users');
  }, [isPrototypesDenied, setActiveSection]);

  // Виджеты, чей источник не хранит версию клиента (AppsFlyer: установки и cost,
  // предрасчитанные ROAS-таблицы, applovin), при активном фильтре версии не
  // показываются вовсе: ни без фильтра, ни с частичным.
  const versionFilterActive = hasVersionFilter(selectedClientVersions, selectedInstallVersions);
  const gateByVersion = (widget) => (versionFilterActive ? <VersionUnavailable /> : widget);

  // Прогрев соседних вкладок раздела «Пользователи» — переключение вкладки
  // бьёт в клиентский TTL-кэш вместо повторного запроса к ClickHouse.
  useUsersSectionPrefetch(activeSection === 'users');

  // Конфигурация вкладок для раздела "Пользователи"
  const usersTabs = [
    {
      id: 'registrations',
      label: 'Регистрации',
      icon: <Calendar className="h-3 w-3" />
    },
    {
      id: 'dau-mau',
      label: 'DAU/MAU',
      icon: <Users className="h-3 w-3" />
    },
    {
      id: 'revenue',
      label: 'Доходы',
      icon: <DollarSign className="h-3 w-3" />
    },
    {
      id: 'arpdau',
      label: 'ARPDAU',
      icon: <TrendingUp className="h-3 w-3" />
    },
    {
      id: 'whales',
      label: 'Киты',
      icon: <Crown className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "Инапы"
  // Вьюхи (общий / по уровням / по лайфтайму) переключаются ВНУТРИ виджета,
  // поэтому здесь одна вкладка.
  const iapTabs = [
    {
      id: 'top-iap',
      label: 'Топ инапов',
      icon: <ShoppingCart className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "Ресурсы" — один график, вкладка одна
  const resourcesTabs = [
    {
      id: 'resource-flow',
      label: 'Движение ресурсов',
      icon: <Gem className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "Реклама" (T1-T11 спеки ad monetization).
  // Порядок — по приоритету спеки: сначала P0 (здоровье, воронка, плейсменты),
  // затем P1/P2. T12 (воронка доступности) не реализован: нет событий request/no-fill.
  const adsTabs = [
    { id: 'ad-health', label: 'Здоровье', icon: <Gauge className="h-3 w-3" /> },
    { id: 'ad-funnel', label: 'Воронка', icon: <Filter className="h-3 w-3" /> },
    { id: 'ad-placements', label: 'Плейсменты', icon: <MapPin className="h-3 w-3" /> },
    { id: 'ad-levels', label: 'По уровням', icon: <BarChart3 className="h-3 w-3" /> },
    { id: 'ad-ltv', label: 'Ad LTV', icon: <TrendingUp className="h-3 w-3" /> },
    { id: 'ad-sources', label: 'Источники', icon: <Coins className="h-3 w-3" /> },
    { id: 'ad-segments', label: 'Сегменты', icon: <Layers className="h-3 w-3" /> },
    { id: 'ad-versions', label: 'Версии', icon: <Smartphone className="h-3 w-3" /> },
    { id: 'ad-rewards', label: 'Награды', icon: <Gift className="h-3 w-3" /> },
    { id: 'ad-frequency', label: 'Частота', icon: <Repeat className="h-3 w-3" /> }
  ];

  // Порядок вкладок «Рекламы» до удаления A/B и «Сетей и eCPM»: по нему читаются
  // числовые индексы, сохранённые в localStorage старыми версиями.
  const legacyAdsTabIds = [
    'ad-health', 'ad-funnel', 'ad-networks', 'ad-placements', 'ad-ltv', 'ad-sources',
    'ad-segments', 'ad-ab', 'ad-versions', 'ad-rewards', 'ad-frequency'
  ];

  // Конфигурация вкладок для раздела "Кампании"
  const companiesTabs = [
    {
      id: 'campaigns',
      label: 'Кампании',
      icon: <Target className="h-3 w-3" />
    },
    {
      id: 'creatives',
      label: 'Креативы',
      icon: <Sparkles className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "LTV"
  const ltvTabs = [
    {
      id: 'ltv',
      label: 'LTV',
      icon: <TrendingUp className="h-3 w-3" />
    },
    {
      id: 'ltv-time',
      label: 'LTV Time',
      icon: <Clock className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "ROAS"
  const roasTabs = [
    {
      id: 'roas',
      label: 'ROAS',
      icon: <BarChart3 className="h-3 w-3" />
    },
    {
      id: 'roas-time',
      label: 'ROAS Time',
      icon: <Activity className="h-3 w-3" />
    },
    {
      id: 'roas-monthly',
      label: 'ROAS Monthly',
      icon: <BarChart3 className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "Сессии"
  const sessionsTabs = [
    {
      id: 'sessions-percentiles',
      label: 'Перцентили',
      icon: <Activity className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "Прогрессия"
  // Вкладка «Графики»: оба графика прогрессии живут на ней стопкой.
  // Вкладка «Воронка старта»: когортная воронка первых шагов (4 блока).
  const progressionTabs = [
    {
      id: 'level-funnel',
      label: 'Воронки уровней',
      icon: <Filter className="h-3 w-3" />
    },
    {
      id: 'level-winrate',
      label: 'Винрейт по дням',
      icon: <BarChart3 className="h-3 w-3" />
    },
    {
      id: 'progression-charts',
      label: 'Графики',
      icon: <LineChart className="h-3 w-3" />
    },
    {
      id: 'start-funnel',
      label: 'Воронка старта',
      icon: <Filter className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "Retention"
  const retentionTabs = [
    {
      id: 'by-period',
      label: 'Ретеншен N-го дня',
      icon: <TrendingUp className="h-3 w-3" />
    },
    {
      id: 'curve',
      label: 'Кривая',
      icon: <LineChart className="h-3 w-3" />
    },
    {
      id: 'cohort',
      label: 'Когорты',
      icon: <Calendar className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "A/B тесты" — один дашборд, вкладка одна
  const abtestTabs = [
    {
      id: 'compare',
      label: 'Сравнение групп',
      icon: <FlaskConical className="h-3 w-3" />
    }
  ];

  // Конфигурация вкладок для раздела "Прототипы"
  const prototypesTabs = [
    {
      id: 'retention',
      label: 'Воронка и ретеншен',
      icon: <RotateCw className="h-3 w-3" />
    },
    {
      id: 'absorption-game',
      label: 'Absorption Game',
      icon: <Target className="h-3 w-3" />
    }
  ];

  // Отдельные хуки для каждого раздела: id вкладок нужны хуку, чтобы положить
  // активную вкладку в ссылку в читаемом виде (?tab=roas-monthly)
  const { activeTab: usersTab, setActiveTab: setUsersTab } = useTabs('users', usersTabs);
  const { activeTab: iapTab, setActiveTab: setIapTab } = useTabs('iap', iapTabs);
  const { activeTab: resourcesTab, setActiveTab: setResourcesTab } = useTabs('resources', resourcesTabs);
  const { activeTab: adsTab, setActiveTab: setAdsTab } = useTabs('ads', adsTabs, legacyAdsTabIds);
  const { activeTab: companiesTab, setActiveTab: setCompaniesTab } = useTabs('companies', companiesTabs);
  const { activeTab: ltvTab, setActiveTab: setLtvTab } = useTabs('ltv', ltvTabs);
  const { activeTab: roasTab, setActiveTab: setRoasTab } = useTabs('roas', roasTabs);
  const { activeTab: sessionsTab, setActiveTab: setSessionsTab } = useTabs('sessions', sessionsTabs);
  const { activeTab: progressionTab, setActiveTab: setProgressionTab } = useTabs('progression', progressionTabs);
  const { activeTab: retentionTab, setActiveTab: setRetentionTab } = useTabs('retention', retentionTabs);
  const { activeTab: abtestTab, setActiveTab: setAbtestTab } = useTabs('abtest', abtestTabs);
  const { activeTab: prototypesTab, setActiveTab: setPrototypesTab } = useTabs('prototypes', prototypesTabs);

  // Рендер контента для раздела "Пользователи"
  const renderUsersContent = () => {
    switch (usersTab) {
      case 0: // Регистрации (AppsFlyer installs)
        return gateByVersion(<RegistrationsWidget />);
      case 1: // DAU/MAU
        return <ActiveUsersWidget />;
      case 2: // Доходы
        return <RevenueWidget />;
      case 3: // ARPDAU
        return <ArpdauWidget />;
      case 4: // Киты (фильтруются только по датам)
        return gateByVersion(<WhalesWidget />);
      default:
        return gateByVersion(<RegistrationsWidget />);
    }
  };

  // Рендер контента для раздела "Инапы" — одна вкладка, вьюхи внутри виджета
  const renderIapContent = () => <TopIapWidget />;

  // Рендер контента для раздела "Ресурсы" — одна вкладка, параметры внутри виджета
  const renderResourcesContent = () => <ResourceFlowWidget />;

  // Рендер контента для раздела "Реклама" — вкладка на дашборд спеки
  const renderAdsContent = () => {
    // Переключение по id, а не по индексу: удаление вкладок сдвигает номера,
    // а id живёт в localStorage/URL у существующих пользователей.
    switch (adsTabs[adsTab]?.id) {
      case 'ad-funnel': return <AdFunnelWidget />;
      case 'ad-placements': return <AdPlacementWidget />;
      case 'ad-levels': return <AdLevelsTab />;
      case 'ad-ltv': return <AdLtvWidget />;
      case 'ad-sources': return gateByVersion(<AdSourceWidget />); // cost из AppsFlyer
      case 'ad-segments': return <AdSegmentsWidget />;
      case 'ad-versions': return <AdVersionWidget />;
      case 'ad-rewards': return <AdRewardWidget />;
      case 'ad-frequency': return <AdFrequencyWidget />;
      default: return gateByVersion(<AdHealthWidget />); // ad-engaged users из applovin без версии
    }
  };

  // Рендер контента для раздела "Кампании"
  const renderCompaniesContent = () => {
    switch (companiesTab) {
      case 0: // Кампании
        return gateByVersion(<CampaignsWidget />);
      case 1: // Креативы
        return gateByVersion(<CreativesWidget />);
      default:
        return gateByVersion(<CampaignsWidget />);
    }
  };

  // Рендер контента для раздела "LTV"
  const renderLtvContent = () => {
    switch (ltvTab) {
      case 0: // LTV (когорта и cost из AppsFlyer)
        return gateByVersion(<LtvWidget />);
      case 1: // LTV Time
        return gateByVersion(<LtvTimeWidget />);
      default:
        return gateByVersion(<LtvWidget />);
    }
  };

  // Рендер контента для раздела "ROAS"
  const renderRoasContent = () => {
    switch (roasTab) {
      case 0: // ROAS (предрасчитанные таблицы и cost без версии)
        return gateByVersion(<RoasWidget />);
      case 1: // ROAS Time
        return gateByVersion(<RoasTimeWidget />);
      case 2: // ROAS Monthly
        return gateByVersion(<RoasMonthlySummaryWidget />);
      default:
        return gateByVersion(<RoasWidget />);
    }
  };

  // Рендер контента для раздела "Сессии"
  const renderSessionsContent = () => {
    switch (sessionsTab) {
      case 0: // Перцентили
        return <SessionsAnalyticsWidget />;
      default:
        return <SessionsAnalyticsWidget />;
    }
  };

  // Рендер контента для раздела "Прогрессия" — оба графика на одной вкладке
  const renderProgressionContent = () => {
    // Переключение по id, а не по индексу: порядок вкладок меняется, а id живёт
    // в localStorage/URL у существующих пользователей.
    switch (progressionTabs[progressionTab]?.id) {
      case 'start-funnel':
        return <StartFunnelWidget />;
      case 'level-winrate':
        return <LevelWinrateByDayWidget />;
      case 'progression-charts':
        return (
          <div className="space-y-6">
            <ProgressionLevelWidget />
            <ProgressionTowerWidget />
          </div>
        );
      default: // Воронки уровней
        return <LevelFunnelWidget />;
    }
  };

  // Рендер контента для раздела "Retention"
  const renderRetentionContent = () => {
    switch (retentionTab) {
      case 0: // Ретеншен N-го дня
        return <RetentionByPeriodWidget />;
      case 1: // Кривая
        return <RetentionCurveWidget />;
      case 2: // Когорты
        return <RetentionCohortWidget />;
      default:
        return <RetentionByPeriodWidget />;
    }
  };

  // Рендер контента для раздела "A/B тесты" — одна вкладка
  const renderAbtestContent = () => {
    switch (abtestTab) {
      case 0: // Сравнение групп
        return <AbCompareWidget />;
      default:
        return <AbCompareWidget />;
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'users':
        return (
          <TabsContainer
            tabs={usersTabs}
            activeTab={usersTab}
            onTabChange={setUsersTab}
          >
            {renderUsersContent()}
          </TabsContainer>
        );
      case 'iap':
        return (
          <TabsContainer
            tabs={iapTabs}
            activeTab={iapTab}
            onTabChange={setIapTab}
          >
            {renderIapContent()}
          </TabsContainer>
        );
      case 'resources':
        return (
          <TabsContainer
            tabs={resourcesTabs}
            activeTab={resourcesTab}
            onTabChange={setResourcesTab}
          >
            {renderResourcesContent()}
          </TabsContainer>
        );
      case 'ads':
        return (
          <AdsGeoProvider>
            <AdsSectionFilters />
            <TabsContainer
              tabs={adsTabs}
              activeTab={adsTab}
              onTabChange={setAdsTab}
            >
              {renderAdsContent()}
            </TabsContainer>
          </AdsGeoProvider>
        );
      case 'companies':
        return (
          <TabsContainer
            tabs={companiesTabs}
            activeTab={companiesTab}
            onTabChange={setCompaniesTab}
          >
            {renderCompaniesContent()}
          </TabsContainer>
        );
      case 'ltv':
        return (
          <TabsContainer
            tabs={ltvTabs}
            activeTab={ltvTab}
            onTabChange={setLtvTab}
          >
            {renderLtvContent()}
          </TabsContainer>
        );
      case 'roas':
        return (
          <TabsContainer
            tabs={roasTabs}
            activeTab={roasTab}
            onTabChange={setRoasTab}
          >
            {renderRoasContent()}
          </TabsContainer>
        );
      case 'sessions':
        return (
          <TabsContainer
            tabs={sessionsTabs}
            activeTab={sessionsTab}
            onTabChange={setSessionsTab}
          >
            {renderSessionsContent()}
          </TabsContainer>
        );
      case 'progression':
        return (
          <TabsContainer
            tabs={progressionTabs}
            activeTab={progressionTab}
            onTabChange={setProgressionTab}
          >
            {renderProgressionContent()}
          </TabsContainer>
        );
      case 'retention':
        return (
          <TabsContainer
            tabs={retentionTabs}
            activeTab={retentionTab}
            onTabChange={setRetentionTab}
          >
            {renderRetentionContent()}
          </TabsContainer>
        );
      case 'abtest':
        return (
          <TabsContainer
            tabs={abtestTabs}
            activeTab={abtestTab}
            onTabChange={setAbtestTab}
          >
            {renderAbtestContent()}
          </TabsContainer>
        );
      case 'prototypes':
        // Без доступа не монтируем виджет (иначе он дёрнул бы API и получил 403)
        if (isPrototypesDenied || !user?.canViewPrototypes) return null;
        return (
          <TabsContainer
            tabs={prototypesTabs}
            activeTab={prototypesTab}
            onTabChange={setPrototypesTab}
          >
            {prototypesTabs[prototypesTab]?.id === 'absorption-game' ? (
              <AbsorptionGameWidget />
            ) : (
              <PrototypesOverview />
            )}
          </TabsContainer>
        );
      default:
        return (
          <TabsContainer
            tabs={usersTabs}
            activeTab={usersTab}
            onTabChange={setUsersTab}
          >
            {renderUsersContent()}
          </TabsContainer>
        );
    }
  };

  return (
    <div className="flex-1 p-3 sm:p-6 overflow-auto">
      <div className="w-full min-w-0 sm:w-[90%] mx-auto">
        {renderContent()}
      </div>
    </div>
  );
}
